// src/features/global-search/GlobalSearchBar.tsx
//
// Command-palette-style global search bar (Bloomberg-terminal / Linear
// inspired). A self-contained island: drop <GlobalSearchBar /> beneath any
// dashboard header and it owns its own state, data fetching and routing.
//
// Features:
//   * 250ms debounce (useDebounce) so the ultra-fast /global-search
//     endpoint is queried once per pause, not per keystroke.
//   * Full keyboard control: ArrowUp / ArrowDown to move, Enter to open
//     (defaults to the #1 ranked hit), Escape to dismiss.
//   * Click-outside detection to close gracefully.
//   * O(1) client-side routing via React Router — selecting a hit swaps
//     the component island (no full page reload) to the matching deep-dive
//     page, keyed off the asset_class discriminant.
//   * Colour-coded asset-class badges + compact market-cap formatting.
//
// Deep-dive routes (see src/App.tsx) all take a `?symbol=` query param:
//   equity -> /dashboard/stock-info-page
//   etf    -> /dashboard/etf-info-page
//   reit   -> /dashboard/reit-info-page
//   crypto -> /dashboard/crypto-info-page  (backend normalises BTC/BTCUSD)

import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { useDebounce } from '../../shared/hooks/useDebounce';
import { formatCompactMoney } from '../../shared/utils/currency';
import { fetchGlobalSearch, type AssetClass, type SearchResult } from './api';

// Maps an asset class to its deep-dive route. Centralised so the routing
// contract lives in one place. `default` mirrors `equity` as a safe
// fallback for any unexpected class the backend might add later.
const ROUTE_BY_CLASS: Record<AssetClass, string> = {
    equity: '/dashboard/stock-info-page',
    etf: '/dashboard/etf-info-page',
    reit: '/dashboard/reit-info-page',
    crypto: '/dashboard/crypto-info-page',
};

const BADGE_STYLE_BY_CLASS: Record<AssetClass, string> = {
    equity:
        'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400 border-blue-200 dark:border-blue-800',
    etf: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-400 border-purple-200 dark:border-purple-800',
    reit: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800',
    crypto:
        'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-400 border-orange-200 dark:border-orange-800',
};

const hasMarketCap = (val?: number | null): boolean =>
    val != null && Number.isFinite(val) && val > 0;

const getBadgeStyle = (assetClass: AssetClass): string =>
    BADGE_STYLE_BY_CLASS[assetClass] ??
    'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border-slate-200 dark:border-slate-700';

export const GlobalSearchBar = () => {
    const navigate = useNavigate();

    // --- UI state --------------------------------------------------------
    const [query, setQuery] = useState('');
    const [results, setResults] = useState<SearchResult[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);

    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const itemRefs = useRef<(HTMLLIElement | null)[]>([]);

    // Only fire the API call 250ms AFTER the user stops typing.
    const debouncedQuery = useDebounce(query, 250);

    // --- 1. Fetch results when the debounced query changes ---------------
    useEffect(() => {
        let isCancelled = false;

        if (debouncedQuery.trim().length === 0) {
            setResults([]);
            setIsOpen(false);
            return;
        }

        setIsSearching(true);
        fetchGlobalSearch(debouncedQuery)
            .then((data) => {
                if (isCancelled) return;
                setResults(data);
                setIsOpen(true);
                setActiveIndex(-1); // reset keyboard cursor on new results
            })
            .finally(() => {
                if (!isCancelled) setIsSearching(false);
            });

        return () => {
            isCancelled = true;
        };
    }, [debouncedQuery]);

    // --- 2. Close on click outside --------------------------------------
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () =>
            document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // --- 3. Keep the keyboard-highlighted row scrolled into view ---------
    useEffect(() => {
        if (activeIndex >= 0) {
            itemRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' });
        }
    }, [activeIndex]);

    // --- 4. O(1) client-side routing on selection ------------------------
    const handleSelectAsset = (asset: SearchResult) => {
        setIsOpen(false);
        setQuery('');
        setResults([]);
        inputRef.current?.blur();

        const path = ROUTE_BY_CLASS[asset.asset_class] ?? ROUTE_BY_CLASS.equity;
        navigate(`${path}?symbol=${encodeURIComponent(asset.symbol)}`);
    };

    // --- 5. Keyboard navigation -----------------------------------------
    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') {
            setIsOpen(false);
            return;
        }
        if (!isOpen || results.length === 0) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex((prev) =>
                prev < results.length - 1 ? prev + 1 : prev,
            );
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex((prev) => (prev > 0 ? prev - 1 : -1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (activeIndex >= 0 && activeIndex < results.length) {
                handleSelectAsset(results[activeIndex]);
            } else {
                // Enter without arrowing -> open the top-ranked (#1) hit.
                handleSelectAsset(results[0]);
            }
        }
    };

    const showDropdown = isOpen && query.trim().length > 0;

    return (
        <div className="relative w-full z-50" ref={containerRef}>
            {/* Input field */}
            <div className="relative flex items-center group">
                <div className="absolute left-4 flex items-center pointer-events-none">
                    <svg
                        className="w-5 h-5 text-slate-400 group-focus-within:text-blue-500 transition-colors duration-200"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                    </svg>
                </div>

                <input
                    ref={inputRef}
                    type="text"
                    role="combobox"
                    aria-expanded={showDropdown}
                    aria-controls="global-search-listbox"
                    aria-autocomplete="list"
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        if (!isOpen) setIsOpen(true);
                    }}
                    onKeyDown={handleKeyDown}
                    onFocus={() => {
                        if (query.trim().length > 0) setIsOpen(true);
                    }}
                    placeholder="Search any asset by ticker or name (e.g. AAPL, Realty Income, Bitcoin)…"
                    className="w-full pl-12 pr-10 py-3.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 shadow-sm transition-all text-base sm:text-sm"
                    autoComplete="off"
                    spellCheck={false}
                />

                {isSearching && (
                    <div className="absolute right-4 w-5 h-5 rounded-full border-2 border-slate-200 dark:border-slate-700 border-t-blue-500 dark:border-t-blue-400 animate-spin" />
                )}
            </div>

            {/* Results dropdown */}
            {showDropdown && (
                <div className="absolute top-full mt-2 w-full bg-white dark:bg-slate-800 rounded-xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                    {results.length === 0 && !isSearching && (
                        <div className="px-4 py-6 text-center text-slate-500 dark:text-slate-400 text-sm">
                            No matching assets found for &quot;{query}&quot;.
                        </div>
                    )}

                    {results.length > 0 && (
                        <>
                            <ul
                                id="global-search-listbox"
                                role="listbox"
                                className="max-h-[400px] overflow-y-auto py-2 divide-y divide-slate-100 dark:divide-slate-700/50"
                            >
                                {results.map((asset, idx) => (
                                    <li
                                        key={`${asset.symbol}-${idx}`}
                                        ref={(el) => {
                                            itemRefs.current[idx] = el;
                                        }}
                                        role="option"
                                        aria-selected={activeIndex === idx}
                                        onClick={() => handleSelectAsset(asset)}
                                        onMouseEnter={() => setActiveIndex(idx)}
                                        className={`px-5 py-3 cursor-pointer flex items-center justify-between transition-colors ${
                                            activeIndex === idx
                                                ? 'bg-slate-50 dark:bg-slate-700/60'
                                                : 'hover:bg-slate-50 dark:hover:bg-slate-700/30'
                                        }`}
                                    >
                                        {/* Left: symbol + name */}
                                        <div className="flex flex-col overflow-hidden mr-4">
                                            <div className="flex items-baseline gap-2 mb-0.5">
                                                <span className="font-bold text-slate-900 dark:text-white text-base tracking-tight">
                                                    {asset.symbol}
                                                </span>
                                                {asset.exchange &&
                                                    asset.asset_class !==
                                                        'crypto' && (
                                                        <span className="text-[10px] text-slate-400 font-bold tracking-wide uppercase">
                                                            {asset.exchange}
                                                        </span>
                                                    )}
                                            </div>
                                            <span className="text-sm text-slate-500 dark:text-slate-400 truncate max-w-[220px] sm:max-w-[350px]">
                                                {asset.name}
                                            </span>
                                        </div>

                                        {/* Right: badge + market cap */}
                                        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                                            <span
                                                className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded border ${getBadgeStyle(
                                                    asset.asset_class,
                                                )}`}
                                            >
                                                {asset.asset_class}
                                            </span>
                                            {hasMarketCap(asset.market_cap) && (
                                                <span className="text-xs text-slate-400 font-medium">
                                                    {formatCompactMoney(
                                                        asset.market_cap,
                                                        {
                                                            symbol: asset.symbol,
                                                            exchange:
                                                                asset.exchange,
                                                        },
                                                        { maximumFractionDigits: 1 },
                                                    )}
                                                </span>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>

                            {/* Keyboard hint footer — the institutional touch. */}
                            <div className="flex items-center gap-4 px-5 py-2 border-t border-slate-100 dark:border-slate-700/50 text-[11px] text-slate-400 dark:text-slate-500 bg-slate-50/60 dark:bg-slate-900/30">
                                <span className="flex items-center gap-1">
                                    <kbd className="px-1.5 py-0.5 rounded border border-slate-300 dark:border-slate-600 font-sans">
                                        ↑
                                    </kbd>
                                    <kbd className="px-1.5 py-0.5 rounded border border-slate-300 dark:border-slate-600 font-sans">
                                        ↓
                                    </kbd>
                                    to navigate
                                </span>
                                <span className="flex items-center gap-1">
                                    <kbd className="px-1.5 py-0.5 rounded border border-slate-300 dark:border-slate-600 font-sans">
                                        ↵
                                    </kbd>
                                    to open
                                </span>
                                <span className="flex items-center gap-1">
                                    <kbd className="px-1.5 py-0.5 rounded border border-slate-300 dark:border-slate-600 font-sans">
                                        esc
                                    </kbd>
                                    to dismiss
                                </span>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default GlobalSearchBar;
